import {AxiosError} from 'axios';
import {QueryKey, UseMutationOptions, useQueryClient} from '@tanstack/react-query';

type Context<T> = {
  previous?: [QueryKey, T | undefined][];
}

type Options<T, V> = Partial<UseMutationOptions<unknown, AxiosError, V, Context<T>>> & {
  optimisticUpdateQueryKey: QueryKey;
  onOptimisticUpdate(input: T | undefined, variables: V): T | undefined;
}

export function useOptimisticUpdate<T, V>(options: Options<T, V>): Options<T, V> {
  const queryKey = options.optimisticUpdateQueryKey;
  const queryClient = useQueryClient();

  return {
    ...options,
    mutationKey: queryKey,
    async onMutate(variables) {
      options.onMutate?.(variables);
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueriesData<T>({ queryKey });
      queryClient.setQueriesData<T>({ queryKey }, (input) => options.onOptimisticUpdate(input, variables));

      return { previous };
    },
    onError(error, variables, context) {
      options.onError?.(error, variables, context);
      for (const [key, data] of context?.previous ?? []) {
        queryClient.setQueryData(key, data);
      }
    },
    onSettled(data, error, variables, context) {
      options.onSettled?.(data, error, variables, context);
      if (queryClient.isMutating({ mutationKey: queryKey }) <= 1) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  };
}
